/**
 * Video Visibility Manager
 * ────────────────────────
 * Corrige les points 1, 2 et (partiellement) 4 de l'audit perf sept. 2026
 * (shop.lemansfc.fr) :
 *
 *  1. CRITIQUE — les <video autoplay loop> continuent de décoder même quand
 *     l'onglet est masqué. Chrome throttle les timers JS en arrière-plan,
 *     PAS le décodage GPU/média : c'est la cause du lag "ordi qui rame"
 *     alors que le profiler JS est à 98 % idle.
 *  2. CRITIQUE — la vidéo hover d'une carte collection (1080p) jouait dès
 *     le chargement de page, même à 2278px sous le fold et invisible
 *     (opacity:0). L'attribut HTML `autoplay` démarre le décodage sans
 *     attendre le survol — indépendant de tout CSS.
 *  4. HAUTE — le marquee de l'announcement-bar (animation CSS compositor)
 *     continue de tourner en arrière-plan (géré ici + section-announcement-bar.css).
 *
 * Stratégie :
 *  - Toute vidéo `autoplay` ambiante (hero mosaïque, carte collection en
 *    lecture continue, mega-menu promo, countdown…) est pausée dès qu'elle
 *    sort du viewport, et reprise uniquement si : toujours visible, onglet
 *    actif, ET (pour les slides hero) sur la slide active.
 *  - Sur onglet masqué : pause immédiate de TOUTES les vidéos en lecture,
 *    quelle que soit leur nature. Au retour, seules les vidéos ambiantes
 *    encore visibles reprennent — jamais les vidéos "hover-layer".
 *  - Les vidéos "hover-layer" (cartes collection) n'ont plus l'attribut
 *    `autoplay` (cf. snippets/collection-card-portrait.liquid) : elles ne
 *    jouent qu'au survol/focus, gérées ici via mouseenter/mouseleave.
 */
(function () {
  'use strict';

  function isSlideActive(video) {
    var slide = video.closest('.hero__slide');
    return !slide || slide.classList.contains('hero__slide--active');
  }

  function isInViewport(el) {
    var r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight &&
           r.right > 0 && r.left < window.innerWidth;
  }

  /* ── 0) Sources posées à l'approche de l'écran ──
   *
   * `autoplay` fait télécharger une vidéo dès le chargement de la page :
   * l'attribut `preload="none"` ne pèse rien face à lui, et la mise en pause
   * hors-écran ci-dessous arrive trop tard, les octets sont déjà partis. Les
   * éléments marqués `data-video-defer` sortent donc du HTML sans URL
   * jouable ; on les leur donne quand ils approchent.
   *
   * La mosaïque du hero fait la même chose de son côté, avec un échelonnement
   * qui lui est propre (section-hero.js) : d'où le marqueur explicite plutôt
   * qu'une recherche de tous les `data-src` de la page.
   */
  function hydrateVideo(video) {
    if (video._hydrated) return;
    video._hydrated = true;

    var sources = video.querySelectorAll('source[data-src]');
    if (sources.length) {
      sources.forEach(function (s) {
        s.setAttribute('src', s.dataset.src);
        s.removeAttribute('data-src');
      });
    } else if (video.dataset.src) {
      video.setAttribute('src', video.dataset.src);
      video.removeAttribute('data-src');
    } else {
      return;
    }

    video.load();
    video.removeAttribute('data-video-defer');
  }

  function observeDeferredVideo(video) {
    if (video._deferObserved) return;
    video._deferObserved = true;

    if (!('IntersectionObserver' in window)) { hydrateVideo(video); return; }

    var observer = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      hydrateVideo(video);
    }, { rootMargin: '300px 0px' });

    observer.observe(video);
  }

  function scanDeferredVideos(root) {
    (root || document).querySelectorAll('video[data-video-defer]').forEach(observeDeferredVideo);
  }

  scanDeferredVideos();

  /* ── 1) Pause hors-écran (scroll) pour les vidéos ambiantes ── */
  function observeAmbientVideo(video) {
    if (video._visObserved) return;
    video._visObserved = true;

    var observer = new IntersectionObserver(function (entries) {
      var visible = entries[0].isIntersecting;
      if (visible && isSlideActive(video) && !document.hidden) {
        if (video.paused && video.readyState >= 2) {
          video.play().catch(function () {});
        }
      } else if (!video.paused) {
        video.pause();
      }
    }, { rootMargin: '0px' });

    observer.observe(video);
  }

  function scanAmbientVideos(root) {
    (root || document).querySelectorAll('video[autoplay]').forEach(observeAmbientVideo);
  }

  scanAmbientVideos();

  /* ── 2) Pause globale onglet masqué + reprise ciblée au retour ── */
  var pausedByTabHide = new Set();

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      document.documentElement.classList.add('is-tab-hidden');
      document.querySelectorAll('video').forEach(function (video) {
        if (!video.paused) {
          pausedByTabHide.add(video);
          video.pause();
        }
      });
    } else {
      document.documentElement.classList.remove('is-tab-hidden');
      pausedByTabHide.forEach(function (video) {
        // Seules les vidéos ambiantes (autoplay natif) reprennent — jamais
        // les vidéos hover-layer, qui attendent un survol explicite.
        if (!video.autoplay) return;
        if (!isSlideActive(video)) return;
        if (isInViewport(video)) video.play().catch(function () {});
      });
      pausedByTabHide.clear();
    }
  });

  /* ── 3) Cartes collection : lecture uniquement au survol / focus ── */
  function bindHoverVideo(card) {
    if (card._hoverVideoBound) return;
    card._hoverVideoBound = true;

    var video = card.querySelector('video.coll-card__hover-layer');
    if (!video) return;

    var play  = function () { if (!document.hidden) video.play().catch(function () {}); };
    var pause = function () { video.pause(); };

    card.addEventListener('mouseenter', play);
    card.addEventListener('mouseleave', pause);
    card.addEventListener('focusin', play);
    card.addEventListener('focusout', pause);
  }

  function scanHoverCards(root) {
    (root || document).querySelectorAll('.coll-card').forEach(bindHoverVideo);
  }

  scanHoverCards();

  /* ── Ré-application après edits du theme editor / sections dynamiques ── */
  document.addEventListener('shopify:section:load', function (e) {
    scanDeferredVideos(e.target);
    scanAmbientVideos(e.target);
    scanHoverCards(e.target);
  });
})();
